import { FaLinkedin } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { Users, Building2, GraduationCap, Search, Briefcase, Loader, UserX, Layers, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LinkedInHandle } from "@/components/worker/WorkerCell";
import { formatRelativeFr, getInitials } from "@/components/worker/workerFormatters";
import { QueueSummaryResponse, QueueSummarySection, InflightByWorkerResponse } from "@/services/workerApi";
import { PluginStatus } from "@/services/pluginStatusApi";

// ============================================================================
// « Flux des tâches en temps réel ».
// Files (sources) → UNE CASE PAR PLUGIN CONNECTÉ → En traitement, reliées par
// des traits fins statiques (SVG). Quand un plugin traite une tâche, sa carte
// reçoit un CONTOUR coloré animé (dégradé conique en rotation lente) le temps
// de la tâche ; au survol, un TOOLTIP affiche la/les tâche(s) en cours.
// 100 % React + Tailwind + shadcn Tooltip (aucun canvas).
// ============================================================================

const MAX_WORKER_CARDS = 5;
const AMBER_TRIPLET = "38 92% 50%";
const EMERALD_TRIPLET = "160 60% 42%";
const FAMINE_CANDIDATS_MIN = 200;
const STARVED_KEYS = ["company_enrichment", "school_enrichment"];

const QUEUES: { key: keyof QueueSummaryResponse; label: string; icon: LucideIcon; cssVar: string }[] = [
  { key: "enrichment", label: "Candidats", icon: Users, cssVar: "--chart-1" },
  { key: "company_enrichment", label: "Entreprises", icon: Building2, cssVar: "--chart-2" },
  { key: "school_enrichment", label: "Écoles", icon: GraduationCap, cssVar: "--chart-3" },
  { key: "company_jobs", label: "Offres", icon: Briefcase, cssVar: "--chart-4" },
  { key: "search", label: "Recherche", icon: Search, cssVar: "--chart-5" },
];

// Chips « action en cours » d'un plugin : une par file du pool in-flight.
// L'ordre et les clés suivent POOL_QUEUE_ORDER (workerApi) — toute file du pool
// doit y figurer, sinon une tâche comptée dans `total` n'aurait aucune pastille.
const CHIPS: { key: "enrichment" | "company_enrichment" | "school_enrichment" | "company_jobs" | "search"; label: string; cssVar: string }[] = [
  { key: "enrichment", label: "Candidats", cssVar: "--chart-1" },
  { key: "company_enrichment", label: "Entreprises", cssVar: "--chart-2" },
  { key: "school_enrichment", label: "Écoles", cssVar: "--chart-3" },
  { key: "company_jobs", label: "Offres d'emploi", cssVar: "--chart-4" },
  { key: "search", label: "Recherche", cssVar: "--chart-5" },
];

// Libellés FR du dernier type de job (plugin au repos).
const LAST_JOB_LABELS: Record<string, string> = {
  campaign_action: "Action campagne",
  enrichment: "Enrichissement candidat",
  search: "Recherche",
  company_enrichment: "Enrichissement entreprise",
  school_enrichment: "Enrichissement école",
  company_jobs: "Offres d'emploi",
  profile_rescrape: "Re-scrape profil",
  quota_blocked: "Quota saturé",
};

type Pt = { x: number; y: number };
type Anchors = { sources: Pt[]; workers: Pt[]; done: Pt };

type WorkerNode = {
  id: string;
  name: string;
  anon: boolean;
  // Compte LinkedIn rattaché au worker (remonté par le heartbeat) — sert à
  // identifier QUI scrape, en particulier quand le plugin est en mode anonyme.
  linkedin?: { firstName?: string; lastName?: string; publicIdentifier?: string; occupation?: string } | null;
  version?: string | null;
  counts: Record<string, number>;
  total: number;
  nearestDeadline?: number | null;
  lastJob?: string | null;
  orphan: boolean;
  active: boolean;
  aggregate?: { extra: number };
};

function computeAnchors(w: number, h: number, nWorkers: number): Anchors {
  const padY = 40;
  const sources = QUEUES.map((_, i) => ({ x: 90, y: 56 + (i * (h - 112)) / Math.max(1, QUEUES.length - 1) }));
  const wx = Math.min(Math.max(w * 0.5, 260), w - 250);
  const workers =
    nWorkers === 0
      ? []
      : Array.from({ length: nWorkers }, (_, i) => ({
          x: wx,
          y: nWorkers > 1 ? padY + (i * (h - 2 * padY)) / (nWorkers - 1) : h / 2,
        }));
  return { sources, workers, done: { x: w - 84, y: h / 2 } };
}

// Tracé SVG (courbe quadratique) entre deux ancres.
function curvePath(p0: Pt, p1: Pt): string {
  const cx = (p0.x + p1.x) / 2;
  const cy = p0.y;
  return `M ${p0.x} ${p0.y} Q ${cx} ${cy} ${p1.x} ${p1.y}`;
}

// prefers-reduced-motion : on remplace le contour animé par un contour statique.
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const on = () => setReduce(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
}

function useAnimatedNumber(value: number, duration = 600): number {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  useEffect(() => {
    displayRef.current = display;
  }, [display]);
  useEffect(() => {
    const from = displayRef.current;
    const to = value;
    if (from === to) return;
    let start: number | null = null;
    let raf = 0;
    const tick = (t: number) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
}

// ----- Carte source / sortie (statique) --------------------------------------
function NodePill({
  anchor,
  title,
  count,
  sub,
  colorVar,
  icon: Icon,
  active,
}: {
  anchor: Pt;
  title: string;
  count: number;
  sub?: string;
  colorVar: string;
  icon: LucideIcon;
  active: boolean;
}) {
  const shown = useAnimatedNumber(count);
  return (
    <div className="pointer-events-none absolute z-10 w-[150px] -translate-x-1/2 -translate-y-1/2" style={{ left: anchor.x, top: anchor.y }}>
      <div className="flex items-center gap-2.5 rounded-xl border bg-card px-3 py-2 shadow-sm">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "hsl(var(--muted))", color: colorVar }}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[11px] text-muted-foreground">{title}</div>
          <div className="text-xl font-bold tabular-nums leading-none">{shown.toLocaleString("fr-FR")}</div>
          {sub && <div className="mt-0.5 truncate text-[10px] text-muted-foreground">{sub}</div>}
        </div>
        {active && (
          <span className="ml-auto flex h-2 w-2">
            <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full opacity-75 motion-reduce:animate-none" style={{ background: colorVar }} />
            <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: colorVar }} />
          </span>
        )}
      </div>
    </div>
  );
}

// ----- Carte plugin : contour actif animé + tooltip de la tâche en cours -----
function WorkerCard({
  anchor,
  node,
  reduce,
  dense,
}: {
  anchor: Pt;
  node: WorkerNode;
  reduce: boolean;
  dense: boolean;
}) {
  const li = node.linkedin;
  const liName = li ? `${li.firstName ?? ""} ${li.lastName ?? ""}`.trim() : "";
  const liVanity = li?.publicIdentifier?.trim() ?? "";
  // Plugin anonyme : on affiche le nom du compte LinkedIn rattaché → on sait QUI scrape.
  const displayName = node.anon && liName ? liName : node.name;
  const initials = getInitials(displayName);
  const spin = node.active && !reduce;
  // Couleur du dégradé conique animé (lock orphelin → rouge).
  const ringColor = node.orphan ? "hsl(var(--destructive))" : "hsl(var(--primary))";
  // Couleur du cadre 2px HORS animation : rouge si verrou expiré, primaire si
  // actif en reduced-motion, sinon la bordure neutre habituelle.
  const ringBg = node.orphan
    ? "hsl(var(--destructive))"
    : node.active && reduce
      ? "hsl(var(--primary))"
      : "hsl(var(--border))";
  const deadlineRel =
    node.active && node.nearestDeadline != null
      ? formatRelativeFr(new Date(node.nearestDeadline * 1000).toISOString())
      : null;
  const tasks = CHIPS.filter((c) => (node.counts[c.key] ?? 0) > 0);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="absolute z-10 w-[210px] -translate-x-1/2 -translate-y-1/2 cursor-help"
          style={{ left: anchor.x, top: anchor.y }}
        >
          {/* Le cadre 2px EST le « contour » : couleur statique (ringBg) ; quand le
              plugin traite une tâche, un dégradé conique tourne par-dessus, clippé
              au cadre arrondi (jamais de débordement dans les coins). */}
          <div className="relative rounded-xl p-[2px] shadow-sm" style={{ background: ringBg }}>
            {spin && (
              <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
                <span
                  className="absolute left-1/2 top-1/2 aspect-square w-[200%] -translate-x-1/2 -translate-y-1/2 animate-spin [animation-duration:4s]"
                  style={{ background: `conic-gradient(from 0deg, transparent, ${ringColor}, transparent 55%)` }}
                />
              </span>
            )}
            <div
              className={cn(
                "relative flex items-center gap-2 rounded-[10px] bg-card px-2.5 py-1.5",
                !node.active && "opacity-80",
              )}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                {node.aggregate ? (
                  <Layers className="h-4 w-4" />
                ) : node.anon && li ? (
                  <FaLinkedin className="h-4 w-4 text-[#0A66C2]" />
                ) : node.anon || !initials ? (
                  <UserX className="h-4 w-4" />
                ) : (
                  <span className="text-xs font-semibold">{initials}</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="truncate text-xs font-medium leading-tight">{displayName}</span>
                  {node.version && <span className="shrink-0 text-[9px] text-muted-foreground">v{node.version}</span>}
                </div>
                {liVanity && !dense && (
                  <div className="mt-0.5 max-w-full truncate text-[10px] leading-tight">
                    <LinkedInHandle vanity={liVanity} />
                  </div>
                )}
                {node.aggregate ? (
                  <div className="text-[10px] text-muted-foreground">
                    {node.aggregate.extra} plugin(s) · {node.total} tâche(s)
                  </div>
                ) : node.total > 0 ? (
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {tasks.map((c) => (
                      <span key={c.key} className="inline-flex items-center gap-0.5 text-[10px] tabular-nums">
                        <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(${c.cssVar}))` }} />
                        {node.counts[c.key]}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-[10px] text-muted-foreground">en veille</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1 text-xs">
        <div className="font-medium">
          {displayName}
          {node.version ? ` · v${node.version}` : ""}
        </div>
        {li && (liName || liVanity) && (
          <div className="text-primary-foreground/80">
            Compte LinkedIn : {liName || `in/${liVanity}`}
            {liName && liVanity ? ` (in/${liVanity})` : ""}
            {li.occupation ? ` — ${li.occupation}` : ""}
            {node.anon ? " · plugin anonyme" : ""}
          </div>
        )}
        {node.aggregate ? (
          <div className="text-primary-foreground/80">
            {node.aggregate.extra} plugin(s) · {node.total} tâche(s) en cours
          </div>
        ) : node.total > 0 ? (
          <>
            <div>
              En cours : {tasks.map((c) => `${node.counts[c.key]} ${c.label.toLowerCase()}`).join(" · ")}
            </div>
            {deadlineRel && (
              <div className="text-primary-foreground/80">Échéance la plus proche : {deadlineRel}</div>
            )}
            {node.orphan && (
              <div className="font-medium text-primary-foreground">Verrou expiré — requeue imminent par le reaper</div>
            )}
          </>
        ) : (
          <div className="text-primary-foreground/80">
            En veille
            {node.lastJob && LAST_JOB_LABELS[node.lastJob]
              ? ` · dernière tâche : ${LAST_JOB_LABELS[node.lastJob]}`
              : ""}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function TaskFlowDiagram({
  summary,
  plugins,
  inflight,
}: {
  summary: QueueSummaryResponse | null;
  plugins: PluginStatus[] | null;
  inflight: InflightByWorkerResponse | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const reduce = usePrefersReducedMotion();

  // ---- Modèle live (dérivé des props à chaque rendu) -------------------------
  const candidats = (summary?.enrichment as QueueSummarySection | undefined)?.count ?? 0;

  const sourceModel = QUEUES.map((q) => {
    const pending = (summary?.[q.key] as QueueSummarySection | undefined)?.count ?? 0;
    const active = pending > 0;
    const starved = active && candidats > FAMINE_CANDIDATS_MIN && STARVED_KEYS.includes(q.key as string);
    return { pending, active, starved };
  });

  // Une case par plugin connecté, enrichie de ses tâches in-flight (live).
  const nowSec = Date.now() / 1000;
  const inflightMap = new Map((inflight?.workers ?? []).map((w) => [w.worker_id, w]));
  const allNodes: WorkerNode[] = (plugins ?? []).map((p) => {
    const w = inflightMap.get(p.worker_id);
    const counts = w?.counts ?? {};
    const total = w?.total ?? 0;
    return {
      id: p.worker_id,
      name: p.is_anonymous ? "Anonyme" : p.user_name || p.worker_id.slice(0, 6),
      anon: Boolean(p.is_anonymous),
      linkedin: p.linkedin_user ?? null,
      version: p.plugin_version,
      counts,
      total,
      nearestDeadline: w?.nearest_deadline ?? null,
      lastJob: p.last_job_type ?? null,
      orphan: w?.nearest_deadline != null && w.nearest_deadline < nowSec,
      active: total > 0,
    };
  });
  allNodes.sort((a, b) => b.total - a.total);
  const visibleNodes = allNodes.slice(0, MAX_WORKER_CARDS);
  const restNodes = allNodes.slice(MAX_WORKER_CARDS);
  const workerNodes: WorkerNode[] = [...visibleNodes];
  if (restNodes.length > 0) {
    const restTotal = restNodes.reduce((s, n) => s + n.total, 0);
    workerNodes.push({
      id: "__aggregate__",
      name: "Autres plugins",
      anon: false,
      counts: {},
      total: restTotal,
      orphan: false,
      active: restTotal > 0,
      aggregate: { extra: restNodes.length },
    });
  }
  const doneTotal = allNodes.reduce((s, n) => s + n.total, 0);

  // ---- Taille (pour positionner cartes + traits) -----------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const anchors = size.w > 0 ? computeAnchors(size.w, size.h, workerNodes.length) : null;

  return (
    <TooltipProvider delayDuration={150}>
      <div ref={containerRef} className="relative h-[380px] w-full overflow-hidden rounded-md border bg-gradient-to-br from-background to-muted/25">
        {anchors && (
          <>
            {/* Traits statiques files → plugins → en traitement */}
            <svg className="pointer-events-none absolute inset-0" width={size.w} height={size.h} aria-hidden="true">
              {QUEUES.flatMap((q, i) =>
                anchors.workers.map((wp, j) => {
                  const sm = sourceModel[i];
                  const stroke = sm.starved ? `hsl(${AMBER_TRIPLET})` : `hsl(var(${q.cssVar}))`;
                  return (
                    <path
                      key={`s${i}-w${j}`}
                      d={curvePath(anchors.sources[i], wp)}
                      fill="none"
                      style={{ stroke }}
                      strokeOpacity={sm.active ? 0.2 : 0.12}
                      strokeWidth={1.25}
                    />
                  );
                }),
              )}
              {anchors.workers.map((wp, j) => (
                <path
                  key={`w${j}-done`}
                  d={curvePath(wp, anchors.done)}
                  fill="none"
                  style={{ stroke: `hsl(${EMERALD_TRIPLET})` }}
                  strokeOpacity={workerNodes[j]?.active ? 0.24 : 0.12}
                  strokeWidth={1.5}
                />
              ))}
            </svg>

            {QUEUES.map((q, i) => (
              <NodePill
                key={q.key as string}
                anchor={anchors.sources[i]}
                title={q.label}
                count={sourceModel[i].pending}
                sub="en attente"
                colorVar={`hsl(var(${q.cssVar}))`}
                icon={q.icon}
                active={sourceModel[i].active}
              />
            ))}

            {workerNodes.length === 0 ? (
              <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card px-4 py-3 text-sm text-muted-foreground">
                Aucun plugin connecté
              </div>
            ) : (
              workerNodes.map((node, i) => (
                <WorkerCard
                  key={node.id}
                  anchor={anchors.workers[i]}
                  node={node}
                  reduce={reduce}
                  dense={workerNodes.length > 4}
                />
              ))
            )}

            <NodePill
              anchor={anchors.done}
              title="En traitement"
              count={doneTotal}
              sub="en cours"
              colorVar={`hsl(${EMERALD_TRIPLET})`}
              icon={Loader}
              active={doneTotal > 0}
            />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
